import React, { createContext, useState, useCallback, useRef, ReactNode } from 'react';
import { PromptModal } from '@/components/ui/Modal';

interface PromptDialogOptions {
  title: string;
  message: string;
  defaultValue?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  inputType?: 'text' | 'number' | 'email' | 'url';
}

interface PromptDialogContextType {
  showPrompt: (options: PromptDialogOptions) => Promise<string | null>;
}

export const PromptDialogContext = createContext<PromptDialogContextType | undefined>(undefined);

export const PromptDialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<PromptDialogOptions>({
    title: '',
    message: '',
    defaultValue: '',
    placeholder: '',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    inputType: 'text',
  });

  const resolveRef = useRef<((value: string | null) => void) | null>(null);

  const showPrompt = useCallback((promptOptions: PromptDialogOptions): Promise<string | null> => {
    setOptions({
      title: promptOptions.title,
      message: promptOptions.message,
      defaultValue: promptOptions.defaultValue ?? '',
      placeholder: promptOptions.placeholder ?? '',
      confirmText: promptOptions.confirmText ?? 'Confirmar',
      cancelText: promptOptions.cancelText ?? 'Cancelar',
      inputType: promptOptions.inputType ?? 'text',
    });
    setIsOpen(true);

    return new Promise<string | null>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback((value: string) => {
    setIsOpen(false);
    resolveRef.current?.(value);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.(null);
    resolveRef.current = null;
  }, []);

  return (
    <PromptDialogContext.Provider value={{ showPrompt }}>
      {children}
      <PromptModal
        isOpen={isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={options.title}
        message={options.message}
        defaultValue={options.defaultValue}
        placeholder={options.placeholder}
        confirmText={options.confirmText}
        cancelText={options.cancelText}
        inputType={options.inputType}
        zIndex={99999}
      />
    </PromptDialogContext.Provider>
  );
};
